import { useEffect, useState } from 'react';
import { useAuth } from './lib/contextoAuth';
import LandingPage from './LandingPage';
import RegistroEmpresa from './RegistroEmpresa';
import Login from './pages/Login';
import CambioPassword from './pages/CambioPassword';
import ConfirmaTuCorreo, { CanjeandoElEnlace } from './pages/ConfirmaTuCorreo';
import AppShell from './ui/AppShell';
import { asegurarRetorno } from './ops/historial';
import PantallaDelCorreo from './correo/PantallaDelCorreo';
import { entradaDelDestino, llevarAlModulo, pantallaDelCorreo } from './correo/destinos';
import { llevarEnlaceAEscanear } from './accesos/enlaceDeEtiqueta';
import { llevarEnlaceATerminales } from './terminal/enlaceDeTerminal';
import AvisoDeSalida from './ui/AvisoDeSalida';
import DialogoSalirSinGuardar from './ui/DialogoSalirSinGuardar';
import { useSubpantalla } from './ui/navegacionReact';

// EL AVISO DE SALIDA Y «¿SALIR SIN GUARDAR?» · TC-DR-AT (3 oct 2026). Se
// montan UNA vez, al lado de lo que sea que se esté pintando —el acceso,
// el alta o la aplicación—, porque son de la navegación entera y no de
// una pantalla. Sin nada que decir, no pintan nada.
export default function App() {
  return (
    <>
      <Pantallas />
      <AvisoDeSalida />
      <DialogoSalirSinGuardar />
    </>
  );
}

function Pantallas() {
  const { autenticado, usuario, motivoCierre } = useAuth();

  // «ATRÁS» LLEVA A LA PORTADA, no fuera del sitio (carril ops, 19 sep
  // 2026): una sola vez por carga, para la puerta y para la aplicación.
  // Desde TC-DR-AT esto siembra el historial del módulo de navegación:
  // sin sesión, atrás sale a la portada; con sesión, cada pantalla vuelve
  // a la anterior y Mi trabajo avisa antes de salir.
  useEffect(() => { asegurarRetorno(); }, []);

  // LA ETIQUETA LEÍDA CON LA CÁMARA DEL TELÉFONO · AC-15. `/q/<código>`
  // (producto) y `/qp/<código>` (pieza) pasan a `?m=escanear` con la
  // lectura puesta. Sin sesión, la dirección espera a que se entre.
  llevarEnlaceAEscanear();
  // Y LOS DE LA TERMINAL DE PRODUCTO · DISEÑO-F1 D18, contrato v2 §9:
  // `/t/<código>` (vincular), `/t/<código>/<nonce>` (turno) y `/t`
  // (agregar) pasan a `?m=terminales…` / `?m=terminal-turno…`, igual que
  // `/q/`. Sin sesión, la dirección espera a que se entre.
  llevarEnlaceATerminales();

  // EL ENLACE DEL CORREO SE ATIENDE ANTES QUE NADA, con sesión o sin
  // ella. Se abre casi siempre desde el teléfono, donde no hay sesión
  // iniciada: si esto estuviera detrás de `autenticado`, quien abre el
  // correo en el teléfono acabaría en la pantalla de acceso — que es
  // justo lo que todavía no puede pasar del todo.
  const token = tokenDeVerificacion();
  if (token) return <CanjeandoElEnlace token={token} />;

  // LAS DEMÁS LIGAS DE LOS CORREOS · LISTO-PARA-PILOTO (b). La tabla es
  // un dato (`correo/destinosIr.json`). Las del pie —baja, preferencias,
  // «¿te fue útil?», aviso de privacidad— tienen su pantalla, con sesión o
  // sin ella. Las que llevan a una pantalla de la aplicación se traducen a
  // `?m=<módulo>`, que `AppShell` ya lee: sin sesión, espera a que se entre.
  const delCorreo = pantallaDelCorreo(window.location.search, autenticado);
  if (delCorreo) return <PantallaDelCorreo destino={delCorreo} />;
  llevarAlModulo(window.location.search);

  // --- Usuario con sesión iniciada ---
  if (autenticado) {
    // El cambio de contraseña forzado bloquea todo lo demás hasta resolverse.
    if (usuario.debe_cambiar_password) return <CambioPassword forzado />;
    // Y EL CORREO SIN CONFIRMAR BLOQUEA IGUAL. No es cortesía de la
    // pantalla: el servidor le contesta 403 `CORREO_SIN_VERIFICAR` a
    // todo lo demás, así que enseñarle la consola sería enseñarle una
    // consola donde nada funciona y no dice por qué.
    if (usuario.correo_verificado === false) return <ConfirmaTuCorreo />;
    // Consola responsiva: barra lateral en pantalla ancha, cajón en teléfono.
    // El escaneo a una mano se reusa como el módulo "Escanear".
    return <AppShell />;
  }

  // --- Invitado: sitio público + acceso ---
  // A QUIEN SE LE CERRÓ LA SESIÓN SOLA SE LE LLEVA AL ACCESO, no a la
  // página pública · deuda T-a. Quien estaba contando un pasillo quiere
  // volver a entrar, no que le vuelvan a explicar qué es TEGO.
  //
  // Y LA DIRECCIÓN NO SE TOCA: el módulo y los filtros viven en la barra
  // de direcciones desde que se hizo `urlEstado.js`, así que al entrar de
  // nuevo `AppShell` los relee y la persona aparece donde estaba. Eso es
  // «volver donde estabas», y sale gratis por no borrar nada.
  return <Invitado inicio="LOGIN" cerrada={!!motivoCierre} />;
}

/* =====================================================================
   LA PORTADA DE LA CONSOLA YA NO SE PINTA · 30 de agosto de 2026
   =====================================================================
   Desde que el sitio público vive fuera —un archivo estático en `/`—
   había DOS portadas: la nueva y `LandingPage`, que la consola seguía
   pintando en `/app` cuando no había sesión. Se contradecían: una decía
   «Cuenta. Rastrea. Comprueba.» y la otra «Deja de contar cajas a mano».

   Ahora `/app` sin sesión abre **el acceso**, que es lo único a lo que
   se va a `/app` sin sesión. Y quien llegue a una dirección de marketing
   dentro de la consola se manda a `/` con `replace`, para que el botón
   de atrás no rebote entre las dos portadas.

   `LandingPage` NO SE BORRA. Sigue en el árbol con sus cuatro secciones
   y sus fichas propias, y `RENDER_LANDING` la vuelve a encender con una
   línea. Es la regla de no-destrucción: lo que estorba se apaga y se
   marca, no se tira. Y hay una prueba que comprueba que sigue ahí.
   ===================================================================== */

// El interruptor. Apagado desde que el sitio público tomó la raíz;
// encenderlo devuelve la portada de la consola tal como estaba.
export const RENDER_LANDING = false;

// Las direcciones de marketing que quedaron dentro de la consola. Quien
// llegue por un enlace viejo o por un marcador acaba en el sitio nuevo,
// no en una portada que ya no se mantiene.
const DE_MARKETING = ['/landing', '/inicio', '/index.html'];

function esDeMarketing(camino) {
  return DE_MARKETING.includes(camino.replace(/\/+$/, '') || '/');
}

// POR DONDE ENTRA QUIEN VIENE DEL SITIO PUBLICO.
//
// El sitio de marketing vive fuera de la consola desde el 30 de agosto
// de 2026 —es un archivo estatico en `/`— y sus botones tienen que poder
// abrir la pantalla concreta: «Entrar» el acceso, «Comienza gratis» el
// alta de empresa.
//
// Sin esto aterrizaban en la landing DE LA CONSOLA, que es otra pagina
// de marketing: dos seguidas, y la persona que ya pulso «Comienza
// gratis» tendria que volver a pulsarlo.
//
// EL PARAMETRO ES OPCIONAL Y NO CAMBIA NADA SIN EL: sin `?ir=` se
// empieza donde se empezaba. Lo desconocido tambien cae ahi — un enlace
// viejo o mal copiado no puede dejar a nadie en una pantalla en blanco.
// EL ENLACE QUE LLEGA POR CORREO: `/app?ir=verificar&t=<token>`.
//
// Va por la cadena de consulta y no por la ruta, igual que `?ir=login`,
// porque `/app/loquesea` no existe: nginx sirve la consola en `/app` y
// una ruta inventada debajo daría un 404 antes de llegar aquí. Un enlace
// de correo que da 404 es un alta perdida.
//
// SE COMPRUEBA LA FORMA DEL TOKEN antes de mandarlo: un `t` vacío o
// absurdo se trata como si no viniera, y la persona ve el acceso normal
// en vez de un error de algo que no pidió.
function tokenDeVerificacion() {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('ir') !== 'verificar') return null;
    const t = (q.get('t') || '').trim();
    return /^[A-Za-z0-9_-]{20,200}$/.test(t) ? t : null;
  } catch { return null; }
}

function entradaPedida() {
  try {
    const ir = new URLSearchParams(window.location.search).get('ir');
    if (ir === 'login') return 'LOGIN';
    if (ir === 'registro') return 'REGISTRO';
    // Y las demás entradas de la tabla (`activar` del correo de alta).
    return entradaDelDestino(window.location.search);
  } catch { /* sin URLSearchParams no hay peticion que atender */ }
  return null;
}

function Invitado({ inicio = 'LOGIN', cerrada = false }) {
  // ACCESO ← → REGISTRO. La portada ya no está en medio: vive en `/`.
  //
  // LO QUE PIDE LA DIRECCION MANDA, salvo cuando a alguien se le cerro
  // la sesion sola: ese va al acceso aunque el enlace dijera otra cosa
  // (deuda T-a), porque estaba trabajando y quiere volver.
  const [vista, setVista] = useState(
    cerrada ? 'LOGIN' : (entradaPedida() || inicio),
  );
  // Del acceso al alta se va por un botón: atrás vuelve al acceso.
  useSubpantalla('registro', vista === 'REGISTRO', () => setVista('LOGIN'));

  // UNA DIRECCION DE MARKETING DENTRO DE LA CONSOLA SE MANDA AL SITIO.
  //
  // `replace` y no `href`: asi la consola no queda en el historial y el
  // boton de atras del navegador no rebota entre las dos portadas, que
  // es exactamente lo que pasaba mientras hubo dos.
  useEffect(() => {
    if (esDeMarketing(window.location.pathname)) {
      window.location.replace('/');
    }
  }, []);

  if (vista === 'REGISTRO') return <RegistroEmpresa />;
  // LA PORTADA DE LA CONSOLA SOLO SI ALGUIEN LA ENCIENDE. Ver el
  // comentario de `RENDER_LANDING`: el componente sigue entero, apagado.
  if (vista === 'LANDING' && RENDER_LANDING) {
    return <LandingPage onNavigate={setVista} />;
  }
  return <Login onCrearCuenta={() => setVista('REGISTRO')} />;
}

// «Volver al sitio» NO NECESITA CÓDIGO: en el acceso y en el alta es un
// `<a href="/">` de verdad, que además se puede copiar y abrir en otra
// pestaña. Un `onClick` con `location.href` no.
